<template>
	<main>
		<div class="ly_mw-68 ly_col-48">
			<h6>{{ category }}</h6>
			<StickyHideScrollTabBar
				v-if="selectedTab === 'sector'"
				:items="tab ?? []"
				:selected-scroll-tab="selectedScrollTab"
				@update:category="onClickTabBar"
			/>
			<StickyScrollTabBar
				v-else
				:items="tab ?? []"
				:selected-scroll-tab="selectedScrollTab"
				@update:category="onClickTabBar"
			/>
			<div ref="root" class="ly_col-48 c_responsive">
				<EditorBox
					v-for="(item, idx) in marketTimingData?.market_timing?.[category as keyof GetMarketTimingCountry] ?? []"
					:id="item.market_timing_title"
					:key="idx"
					v-intersection-observer="[
						onIntersectionObserver,
						{ rootMargin: displayStore.isDesktop ? '-185px 0px -1000px 0px' : '-50%' },
					]"
					><div v-dompurify-html="item.market_timing_content"></div
				></EditorBox>
			</div>
		</div>
	</main>
</template>
<script setup lang="ts">
// core
import { computed, onMounted, ref, watchEffect } from 'vue';
import { useRoute } from 'vue-router';
import { vIntersectionObserver } from '@vueuse/components';

// api
import { GetMarketTimingCountry, useGetMarketTimingApi } from '@/api/marketTiming';
import StickyHideScrollTabBar from '@/components/atom/tabBar/StickyHideScrollTabBar.vue';
import StickyScrollTabBar from '@/components/atom/tabBar/StickyScrollTabBar.vue';
// component
import EditorBox from '@/components/layout/common/EditorBox.vue';
// store
import { useDisplayStore } from '@/stores/display';

// -----variable-----
const route = useRoute();
const displayStore = useDisplayStore();

const selectedTab = computed(() => {
	return route.path.split('/')[2];
});
const category = computed(() => {
	return selectedTab.value !== 'usa' && selectedTab.value !== 'jpn' 
		? (selectedTab.value === 'sector' ? '업종(한국)' : '한국시장') 
		: selectedTab.value === 'usa' 
			? '미국시장' 
			: '일본시장';
});

const selectedScrollTab = ref();
const tab = ref<string[]>();
const root = ref();
// -----api-----
const { data: marketTimingData } = useGetMarketTimingApi(computed(() => ({ category: category.value })));
// -----function-----
//tab 클릭 시, 해당 화면으로 이동
const movetToContent = (tab: string) => {
	requestAnimationFrame(() => {
		const target = document.getElementById(tab)?.offsetTop ?? 0;
		const headerHeight = document.getElementById('TopHeader')?.offsetHeight ?? 0;
		const targetScroll = target - headerHeight - (category.value === '업종(한국)' ? 100 : 100);
		window.scrollTo({
			top: targetScroll,
		});
	});
};

const onIntersectionObserver = ([entry]: IntersectionObserverEntry[]) => {
	if (entry.isIntersecting) {
		selectedScrollTab.value = entry.target.id;
	}
};

const onClickTabBar = (tab: string) => {
	movetToContent(tab);
};

// tab 리스트 저장
watchEffect(() => {
	if (marketTimingData.value) {
		if (category.value === '업종(한국)') {
			tab.value = marketTimingData.value.market_timing?.['업종(한국)']?.map((item) => item.market_timing_title) || [];
		} else if (category.value === '미국시장') {
			tab.value = marketTimingData.value.market_timing.미국시장?.map((item) => item.market_timing_title) || [];
		} else if (category.value === '일본시장') {
			tab.value = marketTimingData.value.market_timing.일본시장?.map((item) => item.market_timing_title) || [];
		} else {
			tab.value = marketTimingData.value.market_timing.한국시장?.map((item) => item.market_timing_title) ?? [];
		}
	}
});

// 새로고침해도 스크롤 위치 유지
onMounted(() => {
	history.scrollRestoration = 'auto';
});
</script>

<style lang="scss" scoped></style>